import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  calculatePropertyCommission,
  getPropertyCommission,
  updateCommissionStatus,
  getPropertyCostOfSales,
  getPropertyCommissionOverride,
  type CommissionCalculation,
  type CommissionStatus,
} from '../services/commissionService';
import type { PropertyCommissionOverride } from '../types/commission';

interface CommissionCalculationPanelProps {
  agencySlug: string;
  propertyId: string;
  soldPrice?: number;
  commissionRate?: number;
  gstInclusive?: boolean;
  canEdit?: boolean;
  /** When true, agency admin can change commission status (e.g. pending → paid). Default false. */
  canUpdateCommissionStatus?: boolean;
}

const CommissionCalculationPanel: React.FC<CommissionCalculationPanelProps> = ({
  agencySlug,
  propertyId,
  soldPrice,
  commissionRate,
  gstInclusive,
  canEdit = false,
  canUpdateCommissionStatus = false,
}) => {
  const [calculation, setCalculation] = useState<CommissionCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [costOfSalesTotal, setCostOfSalesTotal] = useState<number | null>(null);
  const [override, setOverride] = useState<PropertyCommissionOverride | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const effectiveRate = commissionRate;
  const effectiveGstInclusive = gstInclusive ?? false;

  const loadOverride = useCallback(async () => {
    if (!agencySlug || !propertyId) return;
    try {
      const o = await getPropertyCommissionOverride(agencySlug, propertyId);
      setOverride(o);
    } catch {
      setOverride(null);
    }
  }, [agencySlug, propertyId]);

  useEffect(() => {
    loadOverride();
  }, [loadOverride]);

  const loadCalculation = useCallback(async () => {
    if (!soldPrice || effectiveRate == null) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getPropertyCommission(agencySlug, propertyId);
      setCalculation(result);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setCalculation(null);
      } else {
        setError(err.message || 'Failed to load commission calculation');
      }
    } finally {
      setLoading(false);
    }
  }, [agencySlug, propertyId, soldPrice, effectiveRate]);

  useEffect(() => {
    if (soldPrice && effectiveRate != null) {
      loadCalculation();
    }
  }, [soldPrice, effectiveRate, loadCalculation]);

  useEffect(() => {
    if (!agencySlug || !propertyId || !calculation) {
      setCostOfSalesTotal(null);
      return;
    }
    let cancelled = false;
    getPropertyCostOfSales(agencySlug, propertyId)
      .then((data) => {
        if (!cancelled) setCostOfSalesTotal(data.totalCost ?? 0);
      })
      .catch(() => {
        if (!cancelled) setCostOfSalesTotal(null);
      });
    return () => { cancelled = true; };
  }, [agencySlug, propertyId, calculation]);

  const storedCost = calculation?.costOfSales ?? 0;
  const isStale =
    calculation != null &&
    costOfSalesTotal != null &&
    Math.abs(costOfSalesTotal - storedCost) > 0.001;

  const handleCalculate = async () => {
    if (!soldPrice || effectiveRate == null) {
      setError('Property must have sold price and commission rate (or a property-specific override) to calculate commission');
      return;
    }

    try {
      setCalculating(true);
      setError(null);
      const result = await calculatePropertyCommission(agencySlug, propertyId);
      setCalculation(result);
      setCostOfSalesTotal(result.costOfSales ?? 0);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate commission');
    } finally {
      setCalculating(false);
    }
  };

  const handleStatusChange = async (newStatus: CommissionStatus) => {
    if (!calculation || calculation.status === newStatus) return;
    try {
      setStatusUpdating(true);
      setError(null);
      const result = await updateCommissionStatus(agencySlug, propertyId, newStatus);
      setCalculation(result);
    } catch (err: any) {
      setError(err.message || 'Failed to update commission status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const statusLabel = (s: CommissionStatus) =>
    s === 'pending' ? 'Pending' : s === 'paid' ? 'Paid' : 'Cancelled';
  const statusColor = (s: CommissionStatus) =>
    s === 'paid' ? 'success' : s === 'cancelled' ? 'default' : 'warning';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!soldPrice || effectiveRate == null) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            Set sold price and commission rate for this property to calculate commission. Property-specific
            overrides (agent split only) are managed in Commission Sharing Policy.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            Commission for Assigned Agents
          </Typography>
          {canEdit && (
            <Box>
              <Button
                variant="contained"
                startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
                onClick={handleCalculate}
                disabled={calculating || !soldPrice || effectiveRate == null}
              >
                {calculation ? 'Recalculate' : 'Calculate Commission'}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Cost of sales is deducted before splitting. Recalculate after updating expenses below.
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isStale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cost of sales has been updated (current total: ${(costOfSalesTotal ?? 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}). The amounts below do not yet reflect this. Click <strong>Recalculate</strong> to
            deduct cost of sales and update agency/agent shares.
          </Alert>
        )}

        {calculation ? (
          <Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Original Commission
              </Typography>
              <Typography variant="h5" color="primary.main">
                ${calculation.originalCommission.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Based on ${soldPrice.toLocaleString()} × {effectiveRate}%
                {effectiveGstInclusive ? ' (GST inclusive)' : ' (+ GST)'}
              </Typography>
            </Box>

            {(calculation.costOfSales ?? 0) > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Less: Cost of sales
                </Typography>
                <Typography variant="body1" color="error.main">
                  −${(calculation.costOfSales ?? 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            )}

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Net commission (after cost of sales)
              </Typography>
              <Typography variant="h6">
                ${(calculation.netCommission ?? calculation.originalCommission).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Agency and agent shares are calculated from this amount.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Commission is paid at settlement. This property appears in commission reports only
                after a settlement date is recorded.
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Commission status
              </Typography>
              {canUpdateCommissionStatus ? (
                <FormControl size="small" sx={{ minWidth: 160 }} disabled={statusUpdating}>
                  <InputLabel id="commission-status-label">Status</InputLabel>
                  <Select
                    labelId="commission-status-label"
                    label="Status"
                    value={calculation.status ?? 'pending'}
                    onChange={(e) => {
                      const newStatus = e.target.value as CommissionStatus;
                      if (newStatus === 'cancelled') {
                        setShowCancelConfirm(true);
                        return;
                      }
                      handleStatusChange(newStatus);
                    }}
                  >
                    <MenuItem value="pending">
                      <Box display="flex" alignItems="center" gap={1}>
                        <ScheduleIcon fontSize="small" />
                        Pending
                      </Box>
                    </MenuItem>
                    <MenuItem value="paid">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon fontSize="small" />
                        Paid
                      </Box>
                    </MenuItem>
                    <MenuItem value="cancelled">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CancelIcon fontSize="small" />
                        Cancelled
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  size="small"
                  label={statusLabel((calculation.status ?? 'pending') as CommissionStatus)}
                  color={statusColor((calculation.status ?? 'pending') as CommissionStatus)}
                  variant={calculation.status === 'paid' ? 'filled' : 'outlined'}
                />
              )}
              {calculation.status === 'paid' && calculation.paidAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Marked paid on {new Date(calculation.paidAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  {calculation.paidByName ? ` by ${calculation.paidByName}` : ''}
                </Typography>
              )}
              <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
                <DialogTitle>Mark commission as cancelled?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Only use when the sale did not proceed (e.g. settlement did not complete). This is a rare case, not a normal status like pending or paid.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowCancelConfirm(false)}>Keep status</Button>
                  <Button
                    color="inherit"
                    variant="outlined"
                    onClick={() => {
                      handleStatusChange('cancelled');
                      setShowCancelConfirm(false);
                    }}
                  >
                    Yes, mark as cancelled
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>

            {override && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Property override (read-only)
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                  <Chip
                    size="small"
                    label={
                      override.sharing.agentSplitMethod === 'equal'
                        ? 'Agent split: Equal'
                        : override.sharing.customAgentPercentages
                          ? `Agent split: Custom ${[override.sharing.customAgentPercentages.primary, override.sharing.customAgentPercentages.secondary, override.sharing.customAgentPercentages.tertiary].filter((x): x is number => x != null).join('/')}`
                          : 'Agent split: Custom'
                    }
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Managed in Commission Sharing Policy.
                </Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Agency Share</Typography>
                  </Box>
                  <Typography variant="h6">
                    ${calculation.agencyShare.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Total Agent Share</Typography>
                  </Box>
                  <Typography variant="h6">
                    ${calculation.agentShares
                      .reduce((sum, share) => sum + share.shareAmount, 0)
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {calculation.agentShares.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Agent Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Agent</TableCell>
                        <TableCell align="right">Share %</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calculation.agentShares.map((share, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                              {share.agentName}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${share.sharePercentage.toFixed(2)}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ${share.shareAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            Commission has not been calculated yet. Click &quot;Calculate Commission&quot; to split commission
            among the assigned agents. Gross commission is based on sold price and rate; cost of sales
            (see below) is deducted before splitting between agency and agents. Commission is paid at
            settlement; add a settlement date to see this property in commission reports.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CommissionCalculationPanel;
